import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, ArrowLeft, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import MetaTags from '@/components/MetaTags';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white">Post Not Found</h1>
          <Link to="/blog">
            <Button className="mt-6 bg-[#FF2D78]">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="blog-post-page">
      <MetaTags
        title={post.title}
        description={post.excerpt || `${post.title} — a Crawl Baby Crawl city guide.`}
        image={post.featured_image}
        path={`/blog/${post.slug}`}
      />
      {/* Hero */}
      <section className="relative h-[50vh]" data-testid="blog-post-hero">
        <div className="absolute inset-0">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft size={16} />
              Back to Blog
            </Link>
            <Badge className="bg-[#1E90FF] text-white mb-4">
              <MapPin size={12} className="mr-1" />
              {post.city_tag}
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white uppercase">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 mt-4 text-gray-400">
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{post.read_time} min read</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <span>By {post.author_name}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 py-12" data-testid="blog-post-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8 md:p-12">
            <p className="text-xl text-gray-300 leading-relaxed mb-8 border-l-4 border-[#FF2D78] pl-6">
              {post.excerpt}
            </p>
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                {post.content}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-[#FF2D78]/20 to-[#1E90FF]/20 border border-white/10 rounded-lg p-8 text-center">
            <h3 className="font-heading text-2xl text-white uppercase mb-2">Ready to Crawl {post.city_tag}?</h3>
            <p className="text-gray-400 mb-6">Book your spot on our next {post.city_tag} bar crawl.</p>
            <Link to="/crawls">
              <Button className="bg-[#FF2D78] hover:bg-[#ff4d8d] text-white font-bold uppercase tracking-widest px-8">
                Find a Crawl
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default BlogPostPage;
